import React from "react";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import { useQuery, useLazyQuery } from "@apollo/client";
import Cookies from "js-cookie";
import { initI18n, useTranslation, loadResources } from "@tecma/i18n";
import Client from "gqlclient/Client";
import { datadogRum } from "@datadog/browser-rum";

import { useStore } from "store/storeUtils";
import PrivateRoute from "routes/PrivateRoute";
import RouteConsts from "routes/Routes.jsx";
import Login from "pages/Login";
import ForgotPassword from "pages/ForgotPassword/ForgotPassword";
import ChangePassword from "pages/ChangePassword/ChangePassword";
import ActivateAccount from "pages/ActivateAccount/ActivateAccount";
import convertLanguage from "utils/convertLanguage";
import SnackbarError from "components/generic/SnackbarError";
import { LoaderPage } from "@tecma/ds/loader-page";
import DetectUrl from "utils/DetectUrl";
import JwtChecker from "utils/JwtChecker";
import translation from "./i18n/translation/en/translation.json";
import useLogout from "hooks/useLogout";

if (process.env.REACT_APP_NAMESPACE === "biz-tecma-prod") {
  datadogRum.init({
    applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID,
    clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
    // site refers to the Datadog site parameter of your organization
    // see Getting Started with Datadog Sites
    site: "datadoghq.eu",
    service: "bss",
    env: process.env.REACT_APP_NAMESPACE,
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 50,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
  });
}
export const i18n = initI18n(translation, {
  bucketBase: `${process.env.REACT_APP_BUCKET_BASEURL}`,
  productName: "businessplatform",
  apiBase: process.env.REACT_APP_API_URI || "",
  expirationTime: 1000 * 60, // 1 minuto,
  cache: "no-cache",
});

export const App = () => {
  const store = useStore();
  const [loading, setLoading] = React.useState(true);
  const [i18nLoading, seti18nLoading] = React.useState(true);
  const { i18n } = useTranslation();

  const { logout } = useLogout();
  const [loadUserInfo, userInfo] = useLazyQuery(
    Client.GET_USER_INFO,
    Client.GET_USER_INFO_DEFAULT_OPTIONS(store.projectId),
  );

  const skipCondition = () => {
    return ["login", "changePassword", "forgotPassword", "resetPassword"].some((route) =>
      window.location.pathname.includes(route),
    );
  };

  const getUserInfo = (projectId) => {
    if (!Cookies.get("jwt")) {
      store.clearLoggedUser();
      return;
    }
    store.setCheckingJwt(true);
    if ((store.projectId || projectId) && !userInfo.called && !skipCondition()) {
      loadUserInfo();
    }
  };

  React.useEffect(() => {
    getUserInfo();

    // eslint-disable-next-line
  }, [store.projectId]);

  React.useEffect(() => {
    if (userInfo && !userInfo.loading && userInfo.called && userInfo.data?.getUserByJWT) {
      if (!Cookies.get("jwt")) {
        store.clearLoggedUser();
        return;
      }
      store.setCheckingJwt(false);
      store.setLoggedUser(userInfo.data.getUserByJWT);
      const userLanguage = userInfo.data?.getUserByJWT?.language;
      const userLocale = userInfo.data?.getUserByJWT?.locale;
      if (userLanguage && i18n.language.split("-")[0] !== userLanguage) {
        i18n.changeLanguage(convertLanguage(userLanguage, userLocale));
      }
    }
    if (userInfo.data?.errors?.length || userInfo.error) {
      logout();
    }
  }, [userInfo]);

  //obtain browser language
  const prjInfo = useQuery(
    Client.GET_PROJECT_INFO,
    Client.GET_PROJECT_INFO_DEFAULT_OPTIONS(DetectUrl()),
  );
  React.useEffect(() => {
    if (prjInfo?.data?.getProjectInfoByHost === null) {
      throw Error();
    }
    if (
      prjInfo &&
      !prjInfo.loading &&
      prjInfo.called &&
      prjInfo.data &&
      prjInfo.data.getProjectInfoByHost &&
      loading
    ) {
      const project = { ...prjInfo.data.getProjectInfoByHost };
      store.setAssetsByObject(project);
      store.setBaseUrl(prjInfo.data.getProjectInfoByHost.baseurl);
      getUserInfo(project.id);
      document.title = store.pageTitles.businessPlatform
        ? store.pageTitles.businessPlatform
        : `${store.loginProjectName} | Business Platform`;

      setLoading(false);
      Promise.resolve()
        .then(() =>
          loadResources({
            id: project.id,
            displayName: project.displayName,
            languages: store.languages,
          }),
        )
        .catch((error) => console.error("Error while loading translations", error))
        .finally(() => seti18nLoading(false));
    }
  }, [prjInfo]);

  if (loading || i18nLoading || (userInfo.called && userInfo.loading)) {
    return <LoaderPage text='' />;
  }

  return (
    <>
      <SnackbarError />
      <Router basename={store.baseUrl}>
        <JwtChecker />
        <LayoutRoot />
      </Router>
    </>
  );
};

export const LayoutRoot = () => {
  return (
    <Switch>
      <Route exact path='/login' component={Login} />
      <Route exact path='/resetPassword' component={ActivateAccount} />
      <Route exact path='/forgotPassword' component={ForgotPassword} />
      <Route exact path='/changePassword' component={ChangePassword} />
      {/* TUTTE LE ROUTE, ORDINATE PER ORDER */}
      {[...RouteConsts]
        .sort((a, b) => a.order - b.order)
        .map((el) => (
          <PrivateRoute
            breadcrumb={el.breadcrumb}
            exact={el.exact}
            key={el.order}
            path={el.to}
            ToRender={el.component}
            title={el.title}
          />
        ))}

      {/* DEFAULT */}
      <Redirect to='/' />
    </Switch>
  );
};
